import React from 'react';

import { useRouter } from 'next/router';

import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Separator,
} from 'revibe-ui';
import { SpacedContainer } from 'revibe-ui';
import { Accordion } from 'revibe-ui';
import { COMPANY_ROUTES } from 'routes';

import { Layout } from 'modules/common/components/Layout';
import { ShopPage } from 'modules/common/components/ShopPage';

import { Link } from 'ui/components/Link';

export const PrivacyPolicyPage = () => {
  const router = useRouter();
  const { language } = useTranslation();
  const { tu } = useTranslation(I18N_NAMESPACES.PAGE_TITLES);

  if (language === 'it') {
    return (
      <ShopPage
        title={tu('privacy-policy')}
        description="La tua privacy è importante per noi. La politica di Revibe è di rispettare
        la tua privacy per quanto riguarda tutte le informazioni che possiamo raccogliere da te
        attraverso il nostro sito web"
      >
        <Layout minHeight className="mx-auto max-w-5xl">
          <SpacedContainer>
            <h1>
              Privacy Policy di <strong>Revibe</strong>
            </h1>
            <p>Revibe raccoglie alcuni Dati Personali dei propri Utenti.</p>
            <p>
              Questo documento può essere stampato utilizzando il comando di
              stampa presente nelle impostazioni di qualsiasi browser.
            </p>
            <Separator />
            <h2 id="owner_of_the_data">Titolare del Trattamento dei Dati</h2>
            <p>
              REVIBE SAS
              <br />9 RUE DAMRÉMONT, PARIS 75018, FRANCE
            </p>
            <p>
              <strong>Indirizzo email del Titolare:</strong>{' '}
              hello@revibe-upcycling.com
            </p>
            <Separator />
            <h2 id="types_of_data">Tipologie di Dati raccolti</h2>

            <p>
              Fra i Dati Personali raccolti da Revibe, in modo autonomo o
              tramite terze parti, ci sono: Strumento di Tracciamento; Dati di
              utilizzo; Dati comunicati durante l'utilizzo del servizio.
            </p>

            <p>
              Dettagli completi su ciascuna tipologia di dati raccolti sono
              forniti nelle sezioni dedicate di questa privacy policy o mediante
              specifici testi informativi visualizzati prima della raccolta dei
              dati stessi.
              <br />
              I Dati Personali possono essere liberamente forniti dall'Utente o,
              nel caso di Dati di Utilizzo, raccolti automaticamente durante
              l'uso di Revibe.
              <br />
              Se non diversamente specificato, tutti i Dati richiesti da Revibe
              sono obbligatori. Se l'Utente rifiuta di comunicarli, potrebbe
              essere impossibile per Revibe fornire il Servizio. Nei casi in cui
              Revibe indichi alcuni Dati come facoltativi, gli Utenti sono
              liberi di astenersi dal comunicare tali Dati, senza che ciò abbia
              alcuna conseguenza sulla disponibilità del Servizio o sulla sua
              operatività.
              <br />
              Gli Utenti che dovessero avere dubbi su quali Dati siano
              obbligatori, sono incoraggiati a contattare il Titolare.
              <br />
              L'eventuale utilizzo di Cookie - o di altri strumenti di
              tracciamento - da parte di Revibe o dei titolari dei servizi terzi
              utilizzati da Revibe, ove non diversamente precisato, ha la
              finalità di fornire il Servizio richiesto dall'Utente, oltre alle
              ulteriori finalità descritte nel presente documento e nella Cookie
              Policy, se disponibile.
            </p>
            <p>
              L'Utente si assume la responsabilità dei Dati Personali di terzi
              ottenuti, pubblicati o condivisi mediante Revibe e garantisce di
              avere il diritto di comunicarli o diffonderli, liberando il
              Titolare da qualsiasi responsabilità verso terzi.
            </p>
            <Separator />
            <h2 id="place_of_processing">
              Modalità e luogo del trattamento dei Dati raccolti
            </h2>
            <h3>Modalità di trattamento</h3>
            <p>
              Il Titolare adotta le opportune misure di sicurezza volte ad
              impedire l'accesso, la divulgazione, la modifica o la distruzione
              non autorizzate dei Dati Personali. <br />
              Il trattamento viene effettuato mediante strumenti informatici e/o
              telematici, con modalità organizzative e con logiche strettamente
              correlate alle finalità indicate. Oltre al Titolare, in alcuni
              casi, potrebbero avere accesso ai Dati altri soggetti coinvolti
              nell'organizzazione di Revibe (personale amministrativo,
              commerciale, marketing, legali, amministratori di sistema) ovvero
              soggetti esterni (come fornitori di servizi tecnici terzi,
              corrieri postali, hosting provider, società informatiche, agenzie
              di comunicazione) nominati anche, se necessario, Responsabili del
              Trattamento da parte del Titolare. L'elenco aggiornato dei
              Responsabili potrà sempre essere richiesto al Titolare del
              Trattamento.
            </p>
            <h3>Base giuridica del trattamento</h3>
            <p>
              Il Titolare tratta Dati Personali relativi all'Utente in caso
              sussista una delle seguenti condizioni:
            </p>
            <ul>
              <li>
                l'Utente ha prestato il consenso per una o più finalità
                specifiche; Nota: in alcuni ordinamenti il Titolare può essere
                autorizzato a trattare Dati Personali senza che debba sussistere
                il consenso dell'Utente o un'altra delle basi giuridiche
                specificate di seguito, fino a quando l'Utente non si opponga
                (“opt-out”) a tale trattamento. Ciò non è tuttavia applicabile
                qualora il trattamento di Dati Personali sia regolato dalla
                legislazione europea in materia di protezione dei Dati
                Personali;
              </li>
              <li>
                il trattamento è necessario all'esecuzione di un contratto con
                l'Utente e/o all'esecuzione di misure precontrattuali;
              </li>
              <li>
                il trattamento è necessario per adempiere un obbligo legale al
                quale è soggetto il Titolare;
              </li>
              <li>
                il trattamento è necessario per l'esecuzione di un compito di
                interesse pubblico o per l'esercizio di pubblici poteri di cui è
                investito il Titolare;
              </li>
              <li>
                il trattamento è necessario per il perseguimento del legittimo
                interesse del Titolare o di terzi.
              </li>
            </ul>
            <p>
              È comunque sempre possibile richiedere al Titolare di chiarire la
              concreta base giuridica di ciascun trattamento ed in particolare
              di specificare se il trattamento sia basato sulla legge, previsto
              da un contratto o necessario per concludere un contratto.
            </p>
            <h3>Luogo</h3>
            <p>
              I Dati sono trattati presso le sedi operative del Titolare ed in
              ogni altro luogo in cui le parti coinvolte nel trattamento siano
              localizzate. Per ulteriori informazioni, contatta il Titolare.
              <br />I Dati Personali dell'Utente potrebbero essere trasferiti in
              un paese diverso da quello in cui l'Utente si trova. Per ottenere
              ulteriori informazioni sul luogo del trattamento l'Utente può fare
              riferimento alla sezione relativa ai dettagli sul trattamento dei
              Dati Personali.
            </p>
            <p>
              L'Utente ha diritto a ottenere informazioni in merito alla base
              giuridica del trasferimento di Dati al di fuori dell'Unione
              Europea o ad un'organizzazione internazionale di diritto
              internazionale pubblico o costituita da due o più paesi, come ad
              esempio l'ONU, nonché in merito alle misure di sicurezza adottate
              dal Titolare per proteggere i Dati.
              <br />
              <br />
              L'Utente può verificare se abbia luogo uno dei trasferimenti
              appena descritti esaminando la sezione di questo documento
              relativa ai dettagli sul trattamento di Dati Personali o chiedere
              informazioni al Titolare contattandolo agli estremi riportati in
              apertura.
            </p>
            <h3>Periodo di conservazione</h3>
            <p>
              I Dati sono trattati e conservati per il tempo richiesto dalle
              finalità per le quali sono stati raccolti.
            </p>
            <p>Pertanto:</p>
            <ul>
              <li>
                I Dati Personali raccolti per scopi collegati all'esecuzione di
                un contratto tra il Titolare e l'Utente saranno trattenuti sino
                a quando sia completata l'esecuzione di tale contratto.
              </li>
              <li>
                I Dati Personali raccolti per finalità riconducibili
                all'interesse legittimo del Titolare saranno trattenuti sino al
                soddisfacimento di tale interesse. L'Utente può ottenere
                ulteriori informazioni in merito all'interesse legittimo
                perseguito dal Titolare nelle relative sezioni di questo
                documento o contattando il Titolare.
              </li>
            </ul>
            <p>
              Quando il trattamento è basato sul consenso dell'Utente, il
              Titolare può conservare i Dati Personali più a lungo sino a quando
              detto consenso non venga revocato. Inoltre, il Titolare potrebbe
              essere obbligato a conservare i Dati Personali per un periodo più
              lungo in ottemperanza ad un obbligo di legge o per ordine di
              un'autorità.
              <br />
              <br />
              Al termine del periodo di conservazione i Dati Personali saranno
              cancellati. Pertanto, allo spirare di tale termine il diritto di
              accesso, cancellazione, rettificazione ed il diritto alla
              portabilità dei Dati non potranno più essere esercitati.
            </p>
            <Separator />
            <h2 id="use_collected_data">
              Finalità del Trattamento dei Dati raccolti
            </h2>
            <p>
              I Dati dell'Utente sono raccolti per consentire al Titolare di
              fornire il Servizio, adempiere agli obblighi di legge, rispondere
              a richieste o azioni esecutive, tutelare i propri diritti ed
              interessi (o quelli di Utenti o di terze parti), individuare
              eventuali attività dolose o fraudolente, nonché per le seguenti
              finalità: Statistica, Registrazione ed autenticazione e
              Interazione con le piattaforme di live chat.
            </p>

            <p>
              Per ottenere informazioni dettagliate sulle finalità del
              trattamento e sui Dati Personali trattati per ciascuna finalità,
              l'Utente può fare riferimento alla sezione “Dettagli sul
              trattamento dei Dati Personali”.
            </p>
            <Separator />
            <h2 id="data_processing_detailed_info">
              Dettagli sul trattamento dei Dati Personali
            </h2>
            <p>
              I Dati Personali sono raccolti per le seguenti finalità ed
              utilizzando i seguenti servizi:
            </p>
            <Accordion type="multiple">
              <AccordionItem value="analytics">
                <AccordionTrigger>Statistica</AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <p>
                      I servizi contenuti nella presente sezione permettono al
                      Titolare del Trattamento di monitorare e analizzare i dati
                      di traffico e servono a tener traccia del comportamento
                      dell'Utente.
                    </p>

                    <h4>Google Analytics (Google Ireland Limited)</h4>
                    <div>
                      <p>
                        Google Analytics è un servizio di analisi web fornito da
                        Google Ireland Limited (“Google”). Google utilizza i
                        Dati Personali raccolti allo scopo di tracciare ed
                        esaminare l'utilizzo di Revibe, compilare report e
                        condividerli con gli altri servizi sviluppati da Google.
                        <br />
                        Google potrebbe utilizzare i Dati Personali per
                        contestualizzare e personalizzare gli annunci del
                        proprio network pubblicitario.
                      </p>
                    </div>

                    <p>
                      Dati Personali trattati: Dati di utilizzo; Strumento di
                      Tracciamento.
                    </p>

                    <p>
                      Luogo del trattamento: Irlanda -{' '}
                      <Link
                        to="https://policies.google.com/privacy"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>{' '}
                      -{' '}
                      <Link
                        to="https://tools.google.com/dlpage/gaoptout?hl=en"
                        target="_blank"
                      >
                        Opt Out
                      </Link>
                      .
                    </p>

                    <h4>
                      Monitoraggio conversioni di Facebook Ads (pixel di
                      Facebook) (Meta Platforms Ireland Limited)
                    </h4>
                    <div>
                      <p>
                        Il monitoraggio conversioni di Facebook Ads (pixel di
                        Facebook) è un servizio di statistiche fornito da Meta
                        Platforms Ireland Limited che collega i dati provenienti
                        dal network di annunci Meta con le azioni compiute
                        all'interno di Revibe. Il pixel di Facebook monitora le
                        conversioni che possono essere attribuite alle
                        inserzioni di Facebook, Instagram e Audience Network.
                      </p>
                    </div>

                    <p>
                      Dati Personali trattati: Dati di utilizzo; Strumento di
                      Tracciamento.
                    </p>

                    <p>
                      Luogo del trattamento: Irlanda -{' '}
                      <Link
                        to="https://www.facebook.com/about/privacy/"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>
                      .{' '}
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="auth">
                <AccordionTrigger>
                  Registrazione ed autenticazione
                </AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <p>
                      Con la registrazione o l'autenticazione l'Utente consente
                      a Revibe di identificarlo e di dargli accesso a servizi
                      dedicati.
                      <br />
                      A seconda di quanto indicato di seguito, i servizi di
                      registrazione e di autenticazione potrebbero essere
                      forniti con l'ausilio di terze parti. Qualora questo
                      avvenga, Revibe potrà accedere ad alcuni Dati conservati
                      dal servizio terzo usato per la registrazione o
                      l'identificazione. <br />
                      Alcuni dei servizi di seguito indicati potrebbero
                      raccogliere Dati Personali anche per fini di targeting e
                      profilazione; per saperne di più, si prega di fare
                      riferimento alla descrizione di ciascun servizio.
                    </p>

                    <h4>
                      Facebook Authentication (Meta Platforms Ireland Limited)
                    </h4>
                    <div>
                      <p>
                        Facebook Authentication è un servizio di registrazione
                        ed autenticazione fornito da Meta Platforms Ireland
                        Limited e collegato al social network Facebook.
                      </p>
                    </div>

                    <p>
                      Dati Personali trattati: Strumento di Tracciamento; varie
                      tipologie di Dati secondo quanto specificato dalla privacy
                      policy del servizio.
                    </p>

                    <p>
                      Luogo del trattamento: Irlanda -{' '}
                      <Link
                        to="https://www.facebook.com/help/405977429438260"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>
                      .{' '}
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Separator />
            <h2 id="rights_subjects">Diritti dell'Utente</h2>
            <p>
              Gli Utenti possono esercitare determinati diritti con riferimento
              ai Dati trattati dal Titolare.
            </p>
            <p>In particolare, l'Utente ha il diritto di:</p>

            <ul>
              <li>
                <strong>revocare il consenso in ogni momento.</strong> L'Utente
                può revocare il consenso al trattamento dei propri Dati
                Personali precedentemente espresso.{' '}
              </li>
              <li>
                <strong>opporsi al trattamento dei propri Dati.</strong>{' '}
                L'Utente può opporsi al trattamento dei propri Dati quando esso
                avviene su una base giuridica diversa dal consenso. Ulteriori
                dettagli sul diritto di opposizione sono indicati nella sezione
                sottostante.
              </li>
              <li>
                <strong>accedere ai propri Dati.</strong> L'Utente ha diritto ad
                ottenere informazioni sui Dati trattati dal Titolare, su
                determinati aspetti del trattamento ed a ricevere una copia dei
                Dati trattati.
              </li>
              <li>
                <strong>verificare e chiedere la rettificazione.</strong>{' '}
                L'Utente può verificare la correttezza dei propri Dati e
                richiederne l'aggiornamento o la correzione.{' '}
              </li>
              <li>
                <strong>ottenere la limitazione del trattamento.</strong> Quando
                ricorrono determinate condizioni, l'Utente può richiedere la
                limitazione del trattamento dei propri Dati. In tal caso il
                Titolare non tratterà i Dati per alcun altro scopo se non la
                loro conservazione.
              </li>
              <li>
                <strong>
                  ottenere la cancellazione o rimozione dei propri Dati
                  Personali.
                </strong>{' '}
                Quando ricorrono determinate condizioni, l'Utente può richiedere
                la cancellazione dei propri Dati da parte del Titolare.
              </li>
              <li>
                <strong>
                  ricevere i propri Dati o farli trasferire ad altro titolare.
                </strong>{' '}
                L'Utente ha diritto di ricevere i propri Dati in formato
                strutturato, di uso comune e leggibile da dispositivo automatico
                e, ove tecnicamente fattibile, di ottenerne il trasferimento
                senza ostacoli ad un altro titolare. Questa disposizione è
                applicabile quando i Dati sono trattati con strumenti
                automatizzati ed il trattamento è basato sul consenso
                dell'Utente, su un contratto di cui l'Utente è parte o su misure
                contrattuali ad esso connesse.
              </li>
              <li>
                <strong>proporre reclamo.</strong> L'Utente può proporre un
                reclamo all'autorità di controllo della protezione dei dati
                personali competente o agire in sede giudiziale.
              </li>
            </ul>

            <h3>Dettagli sul diritto di opposizione</h3>
            <p>
              Quando i Dati Personali sono trattati nell'interesse pubblico,
              nell'esercizio di pubblici poteri di cui è investito il Titolare
              oppure per perseguire un interesse legittimo del Titolare, gli
              Utenti hanno diritto ad opporsi al trattamento per motivi connessi
              alla loro situazione particolare.
            </p>
            <p>
              Si fa presente agli Utenti che, ove i loro Dati fossero trattati
              con finalità di marketing diretto, possono opporsi al trattamento
              senza fornire alcuna motivazione. Per scoprire se il Titolare
              tratti dati con finalità di marketing diretto gli Utenti possono
              fare riferimento alle rispettive sezioni di questo documento.
            </p>

            <h3>Come esercitare i diritti</h3>
            <p>
              Per esercitare i diritti dell'Utente, gli Utenti possono
              indirizzare una richiesta agli estremi di contatto del Titolare
              indicati in questo documento. Le richieste sono depositate a
              titolo gratuito e evase dal Titolare nel più breve tempo
              possibile, in ogni caso entro un mese.
            </p>
            <Separator />
            <h2 id="cookie_policy">Cookie Policy</h2>
            <p>
              Revibe fa utilizzo di Strumenti di Tracciamento. Per saperne di
              più, l'Utente può consultare la{' '}
              <Link to={COMPANY_ROUTES.COOKIE_POLICY}>Cookie Policy</Link>.
            </p>
            <Separator />
            <h2 id="further_data_processing_info">
              Ulteriori informazioni sul trattamento
            </h2>
            <h3>Difesa in giudizio</h3>
            <p>
              I Dati Personali dell'Utente possono essere utilizzati da parte
              del Titolare in giudizio o nelle fasi preparatorie alla sua
              eventuale instaurazione per la difesa da abusi nell'utilizzo di
              Revibe o dei Servizi connessi da parte dell'Utente.
              <br />
              L'Utente dichiara di essere consapevole che il Titolare potrebbe
              essere obbligato a rivelare i Dati per ordine delle autorità
              pubbliche.
            </p>
            <h3>Informative specifiche</h3>
            <p>
              Su richiesta dell'Utente, in aggiunta alle informazioni contenute
              in questa privacy policy, Revibe potrebbe fornire all'Utente delle
              informative aggiuntive e contestuali riguardanti Servizi
              specifici, o la raccolta ed il trattamento di Dati Personali.
            </p>
            <h3>Log di sistema e manutenzione</h3>
            <p>
              Per necessità legate al funzionamento ed alla manutenzione, Revibe
              e gli eventuali servizi terzi da essa utilizzati potrebbero
              raccogliere log di sistema, ossia file che registrano le
              interazioni e che possono contenere anche Dati Personali, quali
              l'indirizzo IP Utente.
            </p>
            <h3>Informazioni non contenute in questa policy</h3>
            <p>
              Ulteriori informazioni in relazione al trattamento dei Dati
              Personali potranno essere richieste in qualsiasi momento al
              Titolare del Trattamento utilizzando gli estremi di contatto.
            </p>

            <h3>Risposta alle richieste “Do Not Track”</h3>
            <p>
              Revibe non supporta le richieste “Do Not Track”.
              <br />
              Per scoprire se gli eventuali servizi di terze parti utilizzati le
              supportino, l'Utente è invitato a consultare le rispettive privacy
              policy.
            </p>

            <h3>Modifiche a questa privacy policy</h3>
            <p>
              Il Titolare del Trattamento si riserva il diritto di apportare
              modifiche alla presente privacy policy in qualunque momento
              notificandolo agli Utenti su questa pagina e, se possibile, su
              Revibe nonché, qualora tecnicamente e legalmente fattibile,
              inviando una notifica agli Utenti attraverso uno degli estremi di
              contatto di cui è in possesso. Si prega dunque di consultare con
              frequenza questa pagina, facendo riferimento alla data di ultima
              modifica indicata in fondo.
              <br />
              <br />
              Qualora le modifiche interessino trattamenti la cui base giuridica
              è il consenso, il Titolare provvederà a raccogliere nuovamente il
              consenso dell'Utente, se necessario.
            </p>
            <Separator />
            <Accordion type="multiple">
              <AccordionItem value="legal">
                <AccordionTrigger>
                  Definizioni e riferimenti legali
                </AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <h4>Dati Personali (o Dati)</h4>
                    <p>
                      Costituisce dato personale qualunque informazione che,
                      direttamente o indirettamente, anche in collegamento con
                      qualsiasi altra informazione, ivi compreso un numero di
                      identificazione personale, renda identificata o
                      identificabile una persona fisica.
                    </p>

                    <h4>Dati di Utilizzo</h4>
                    <p>
                      Sono le informazioni raccolte automaticamente attraverso
                      Revibe (anche da applicazioni di parti terze integrate in
                      Revibe), tra cui: gli indirizzi IP o i nomi a dominio dei
                      computer utilizzati dall'Utente che si connette con
                      Revibe, gli indirizzi in notazione URI (Uniform Resource
                      Identifier), l'orario della richiesta, il metodo
                      utilizzato nell'inoltrare la richiesta al server, la
                      dimensione del file ottenuto in risposta, il codice
                      numerico indicante lo stato della risposta dal server
                      (buon fine, errore, ecc.) il paese di provenienza, le
                      caratteristiche del browser e del sistema operativo
                      utilizzati dal visitatore, le varie connotazioni temporali
                      della visita (ad esempio il tempo di permanenza su
                      ciascuna pagina) e i dettagli relativi all'itinerario
                      seguito all'interno dell'Applicazione, con particolare
                      riferimento alla sequenza delle pagine consultate, ai
                      parametri relativi al sistema operativo e all'ambiente
                      informatico dell'Utente.
                    </p>

                    <h4>Utente</h4>
                    <p>
                      L'individuo che utilizza Revibe che, salvo ove
                      diversamente specificato, coincide con l'Interessato.
                    </p>

                    <h4>Interessato</h4>
                    <p>
                      La persona fisica cui si riferiscono i Dati Personali.
                    </p>

                    <h4>Responsabile del Trattamento (o Responsabile)</h4>
                    <p>
                      La persona fisica, giuridica, la pubblica amministrazione
                      e qualsiasi altro ente che tratta dati personali per conto
                      del Titolare, secondo quanto esposto nella presente
                      privacy policy.
                    </p>

                    <h4>Titolare del Trattamento (o Titolare)</h4>
                    <p>
                      La persona fisica o giuridica, l'autorità pubblica, il
                      servizio o altro organismo che, singolarmente o insieme ad
                      altri, determina le finalità e i mezzi del trattamento di
                      dati personali e gli strumenti adottati, ivi comprese le
                      misure di sicurezza relative al funzionamento ed alla
                      fruizione di Revibe. Il Titolare del Trattamento, salvo
                      quanto diversamente specificato, è il titolare di Revibe.
                    </p>

                    <h4>Revibe (o questa Applicazione)</h4>
                    <p>
                      Lo strumento hardware o software mediante il quale sono
                      raccolti e trattati i Dati Personali degli Utenti.
                    </p>

                    <h4>Servizio</h4>
                    <p>
                      Il Servizio fornito da Revibe così come definito nei
                      relativi termini (se presenti) su questo
                      sito/applicazione.
                    </p>

                    <h4>Unione Europea (o UE)</h4>
                    <p>
                      Salvo ove diversamente specificato, ogni riferimento
                      all'Unione Europea contenuto in questo documento si
                      intende esteso a tutti gli attuali stati membri
                      dell'Unione Europea e dello Spazio Economico Europeo.
                    </p>

                    <h4>Cookie</h4>
                    <p>
                      I Cookie sono Strumenti di Tracciamento che consistono in
                      piccole porzioni di dati conservate all'interno del
                      browser dell'Utente.
                    </p>

                    <h4>Strumento di Tracciamento</h4>
                    <p>
                      Per Strumento di Tracciamento s'intende qualsiasi
                      tecnologia - es. Cookie, identificativi univoci, web
                      beacons, script integrati, e-tag e fingerprinting - che
                      consenta di tracciare gli Utenti, per esempio raccogliendo
                      o salvando informazioni sul dispositivo dell'Utente.
                    </p>
                    <Separator />
                    <h4>Riferimenti legali</h4>
                    <p>
                      La presente informativa privacy è redatta sulla base di
                      molteplici ordinamenti legislativi, inclusi gli artt. 13 e
                      14 del Regolamento (UE) 2016/679.
                    </p>
                    <p>
                      Ove non diversamente specificato, questa informativa
                      privacy riguarda esclusivamente Revibe.
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </SpacedContainer>
        </Layout>
      </ShopPage>
    );
  }

  if (language === 'fr') {
    return (
      <ShopPage
        title={tu('privacy-policy')}
        description="Votre vie privée est importante pour nous. La politique de Revibe est de respecter
        votre vie privée en ce qui concerne toute information que nous pouvons collecter auprès de vous
        sur notre site web"
      >
        <Layout minHeight className="mx-auto max-w-5xl">
          <SpacedContainer>
            <h1>
              Politique de confidentialité de <strong>Revibe</strong>
            </h1>
            <p>
              Revibe collecte certaines Données personnelles de ses
              Utilisateurs.
            </p>
            <br />
            <p>
              Ce document peut être imprimé à des fins de consultation en
              utilisant la commande d'impression dans les paramètres de tout
              navigateur.
            </p>
            <Separator />
            <h2 id="owner_of_the_data">
              Propriétaire et Responsable du traitement
            </h2>
            <p>
              REVIBE SAS
              <br />9 RUE DAMRÉMONT, PARIS 75018, FRANCE
            </p>
            <p>
              <strong>Courriel de contact du Propriétaire :</strong>{' '}
              hello@revibe-upcycling.com
            </p>
            <Separator />
            <h2 id="types_of_data">Types de Données collectées</h2>

            <p>
              Figurent parmi les types de Données personnelles que Revibe
              collecte directement ou en recourant à des tiers : Traqueur;
              Données d'utilisation; Données communiquées lors de l'utilisation
              du service.
            </p>

            <p>
              Les détails complets sur chaque type de Données personnelles
              collectées sont fournis dans les parties consacrées à la présente
              politique de confidentialité ou par des textes d'explication
              spécifiques publiés avant la collecte des Données.
              <br />
              Les Données personnelles peuvent être librement fournies par
              l'Utilisateur, ou, en cas de Données d'utilisation, collectées
              automatiquement lorsque vous utilisez Revibe.
              <br />
              Sauf indication contraire, toutes les Données demandées par Revibe
              sont obligatoires et leur absence peut rendre impossible la
              fourniture des Services par Revibe. Dans le cas où Revibe précise
              que certaines Données ne sont pas obligatoires, les Utilisateurs
              sont libres de ne pas les communiquer sans entraîner de
              conséquences sur la disponibilité ou le fonctionnement du Service.
              <br />
              Les Utilisateurs qui auraient des doutes sur les Données
              personnelles obligatoires sont invités à contacter le
              Propriétaire.
              <br />
              Toute utilisation des Cookies - ou d'autres outils de suivi - par
              Revibe ou par les propriétaires de services tiers utilisés par
              Revibe vise à fournir le Service demandé par l'Utilisateur, outre
              les autres finalités décrites dans le présent document et dans la
              Politique relative aux cookies, si elle est disponible.
            </p>
            <p>
              Les Utilisateurs sont responsables de toute Donnée personnelle de
              tiers obtenue, publiée ou communiquée par l'intermédiaire de
              Revibe et confirment qu'ils obtiennent le consentement du tiers
              pour fournir les Données au Propriétaire.
            </p>
            <Separator />
            <h2 id="place_of_processing">
              Mode et lieu de traitement des Données
            </h2>
            <h3>Méthodes de traitement</h3>
            <p>
              Le Propriétaire prend les mesures de sécurité appropriées afin
              d'empêcher l'accès, la divulgation, la modification ou la
              destruction non autorisés des Données.
              <br />
              Le traitement des Données est effectué à l'aide d'ordinateurs ou
              d'outils informatiques, en suivant les procédures et les modes
              organisationnels étroitement liés aux finalités indiquées. Outre
              le Propriétaire, les Données peuvent être accessibles, dans
              certains cas, à certaines catégories de personnes en charge du
              fonctionnement de Revibe (administration, ventes, marketing,
              service juridique, administration du système) ou à des parties
              externes (telles que les fournisseurs tiers de services
              techniques, les services de messagerie, les fournisseurs
              d'hébergement, les entreprises informatiques, les agences de
              communication) désignées, le cas échéant, comme Sous-traitantes
              par le Propriétaire. La liste mise à jour de ces parties peut être
              demandée à tout moment au Propriétaire.
            </p>
            <h3>Base juridique du traitement</h3>
            <p>
              Le Propriétaire peut traiter les Données personnelles relatives
              aux Utilisateurs si l'une des conditions suivantes
              s'applique&nbsp;:{' '}
            </p>
            <ul>
              <li>
                les Utilisateurs ont donné leur consentement pour une ou
                plusieurs finalités spécifiques&nbsp;; A noter&nbsp;: Selon
                certaines législations, le Propriétaire peut être autorisé à
                traiter des Données personnelles jusqu'à ce que l'Utilisateur
                s'y oppose («&nbsp;opt-out&nbsp;»), sans avoir à dépendre du
                consentement ou de l'une des bases juridiques suivantes. Cette
                condition ne s'applique toutefois pas lorsque le traitement des
                Données personnelles est soumis à la loi européenne sur la
                protection des données&nbsp;;
              </li>
              <li>
                la fourniture de Données est nécessaire pour l'exécution d'un
                accord avec l'Utilisateur ou pour toute obligation
                précontractuelle de celui-ci&nbsp;;
              </li>
              <li>
                le traitement est nécessaire pour se conformer à une obligation
                légale à laquelle le Propriétaire est soumis&nbsp;;
              </li>
              <li>
                le traitement est lié à une tâche effectuée dans l'intérêt
                public ou dans l'exercice de l'autorité publique dévolue au
                Propriétaire&nbsp;;
              </li>
              <li>
                le traitement est nécessaire aux fins des intérêts légitimes
                poursuivis par le Propriétaire ou par un tiers.
              </li>
            </ul>
            <p>
              Dans tous les cas, le Propriétaire vous aidera volontiers à
              clarifier la base juridique spécifique qui s'applique au
              traitement, et en particulier si la fourniture de Données
              personnelles est une exigence légale ou contractuelle, ou une
              exigence nécessaire pour conclure un contrat.
            </p>
            <h3>Lieu de traitement</h3>
            <p>
              Les Données sont traitées au siège du Propriétaire et dans tous
              les autres lieux où sont situées les parties responsables du
              traitement.
              <br />
              Selon la localisation de l'Utilisateur, les transferts de données
              peuvent entraîner le transfert des Données de ce dernier vers un
              pays autre que le sien. Pour en savoir plus sur le lieu de
              traitement de ces Données transférées, les Utilisateurs peuvent
              consulter la section qui contient des détails sur le traitement
              des Données personnelles.
            </p>
            <p>
              Les Utilisateurs ont également le droit de connaître la base
              juridique des transferts de Données vers un pays situé en dehors
              de l'Union européenne ou vers toute organisation internationale
              régie par le droit international public ou créée par deux pays ou
              plus, comme l'ONU, ainsi que les mesures de sécurité prises par le
              Propriétaire pour sauvegarder leurs Données.
              <br />
              <br />
              Si un tel transfert a lieu, les Utilisateurs peuvent en savoir
              plus en consultant les sections correspondantes du présent
              document ou se renseigner auprès du Propriétaire en utilisant les
              informations fournies dans la section de contact.
            </p>
            <h3>Temps de conservation</h3>
            <p>
              Les Données personnelles sont traitées et conservées aussi
              longtemps que requis pour la finalité pour laquelle elles ont été
              collectées.
            </p>
            <p>Par conséquent&nbsp;:</p>
            <ul>
              <li>
                Les Données personnelles collectées à des fins liées à
                l'exécution d'un contrat entre le Propriétaire et l'Utilisateur
                doivent être conservées jusqu'à la pleine exécution du contrat.
              </li>
              <li>
                Les Données personnelles collectées aux fins des intérêts
                légitimes du Propriétaire doivent être conservées aussi
                longtemps que nécessaire pour atteindre ces objectifs. Les
                Utilisateurs peuvent trouver des informations spécifiques
                concernant les intérêts légitimes poursuivis par le Propriétaire
                dans les sections correspondantes du présent document ou en
                contactant le Propriétaire.
              </li>
            </ul>
            <p>
              Le Propriétaire peut être autorisé à conserver des Données
              personnelles plus longtemps chaque fois que l'Utilisateur a donné
              son consentement à un tel traitement, tant que ce consentement
              n'est pas retiré. En outre, le Propriétaire peut être obligé de
              conserver des Données personnelles plus longtemps chaque fois que
              cela est requis pour l'exécution d'une obligation légale ou sur
              ordre d'une autorité.
              <br />
              <br />
              Une fois la période de conservation expirée, les Données
              personnelles seront supprimées. Par conséquent, le droit d'accès,
              le droit d'effacement, le droit de rectification et le droit à la
              portabilité des données ne peuvent être appliqués après
              l'expiration de la période de conservation.
            </p>
            <Separator />
            <h2 id="use_collected_data">Finalités du traitement</h2>
            <p>
              Les Données relatives à l'Utilisateur sont collectées afin de
              permettre au Propriétaire de fournir son Service, de remplir ses
              obligations, de répondre aux demandes d'application de la loi, de
              protéger ses droits et intérêts (ou ceux de ses Utilisateurs ou de
              tiers), de détecter toute activité malveillante ou frauduleuse,
              ainsi que ce qui suit : Analyses, Inscription et authentification
              et Interaction avec des plateformes de chat en direct.
            </p>

            <p>
              Pour obtenir des informations précises sur les Données
              personnelles utilisées pour chaque finalité, l'Utilisateur peut
              consulter la partie « Informations détaillées sur le traitement
              des Données personnelles ».
            </p>

            <Separator />

            <h2>
              Informations détaillées sur le traitement des Données personnelles
            </h2>
            <p>
              Les Données personnelles sont collectées pour les finalités
              suivantes à l'aide de plusieurs services :
            </p>
            <Accordion type="multiple">
              <AccordionItem value="analytics">
                <AccordionTrigger>Analyses</AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <p>
                      Les services que contient cette partie permettent au
                      Propriétaire de surveiller et d'analyser le trafic Web et
                      de suivre l'évolution du comportement de l'Utilisateur.
                    </p>

                    <h4>Google Analytics (Google Ireland Limited)</h4>
                    <p>
                      Google Analytics est un service d'analyse Web fourni par
                      Google Ireland Limited («&nbsp;Google&nbsp;»). Google
                      utilise les Données collectées pour suivre et analyser
                      l'utilisation de Revibe, préparer des rapports sur ses
                      activités et les partager avec d'autres services Google.
                      Google peut utiliser les Données collectées pour
                      contextualiser et personnaliser les publicités de son
                      propre réseau publicitaire.
                    </p>

                    <p>
                      Données personnelles traitées : Données d'utilisation;
                      Traqueur.
                    </p>

                    <p>
                      Lieu de traitement : Irlande -{' '}
                      <Link
                        to="https://policies.google.com/privacy"
                        target="_blank"
                      >
                        Politique de confidentialité
                      </Link>{' '}
                      -{' '}
                      <Link
                        to="https://tools.google.com/dlpage/gaoptout?hl=fr"
                        target="_blank"
                      >
                        Option de retrait
                      </Link>
                      .{' '}
                    </p>

                    <h4>
                      Suivi des conversions de Facebook Ads (pixel Facebook)
                      (Meta Platforms Ireland Limited)
                    </h4>
                    <div>
                      <p>
                        Le suivi des conversions de Facebook Ads (pixel
                        Facebook) est un service d'analyse fourni par Meta
                        Platforms Ireland Limited, qui relie les données du
                        réseau publicitaire de Meta aux actions effectuées sur
                        Revibe. Le pixel Facebook suit les conversions pouvant
                        être attribuées aux publicités sur Facebook, Instagram
                        et l'Audience Network.
                      </p>
                    </div>

                    <p>
                      Données personnelles traitées : Données d'utilisation;
                      Traqueur.
                    </p>

                    <p>
                      Lieu de traitement : Irlande -{' '}
                      <Link
                        to="https://www.facebook.com/about/privacy/"
                        target="_blank"
                      >
                        Politique de confidentialité
                      </Link>
                      .{' '}
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="auth">
                <AccordionTrigger>
                  Inscription et authentification
                </AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <p>
                      L'inscription ou l'authentification de l'Utilisateur
                      autorise Revibe à l'identifier et lui donne accès à des
                      services dédiés.
                      <br />
                      Selon la description ci-après, les tiers peuvent fournir
                      des services d'inscription et d'authentification. En ce
                      cas, Revibe pourra accéder à des Données stockées par ces
                      tiers à des fins d'inscription ou d'identification. <br />
                      Certains services énumérés ci-dessous peuvent également
                      collecter des Données Personnelles à des fins de ciblage
                      et de profilage. Pour plus d'informations, veuillez
                      consulter la description de chaque service.
                    </p>

                    <h4>
                      Authentication Facebook (Meta Platforms Ireland Limited)
                    </h4>
                    <div>
                      <p>
                        L'authentication Facebook est un service d'inscription
                        et d'authentification fourni par Meta Platforms Ireland
                        Limited qui est connecté au réseau social Facebook.
                      </p>
                    </div>

                    <p>
                      Données personnelles traitées : différents types de
                      Données indiquées dans la politique de confidentialité du
                      service; Traqueur.
                    </p>

                    <p>
                      Lieu de traitement : Irlande -{' '}
                      <Link
                        to="https://www.facebook.com/help/405977429438260"
                        target="_blank"
                      >
                        Politique de confidentialité
                      </Link>
                      .{' '}
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Separator />
            <h2 id="rights_subjects">Droits des Utilisateurs</h2>
            <p>
              Les Utilisateurs peuvent exercer certains droits concernant leurs
              Données traitées par le Propriétaire.
            </p>
            <p>
              En particulier, les Utilisateurs ont le droit de faire ce qui
              suit&nbsp;:
            </p>

            <ul>
              <li>
                <strong>Retirer leur consentement à tout moment.</strong> Les
                Utilisateurs ont le droit de retirer leur consentement s'ils ont
                déjà donné leur consentement au traitement de leurs Données
                personnelles.
              </li>
              <li>
                <strong>S'opposer au traitement de leurs Données.</strong> Les
                Utilisateurs ont le droit de s'opposer au traitement de leurs
                Données si le traitement est effectué sur une base juridique
                autre que le consentement. Des précisions sont ajoutées dans la
                section correspondante ci-dessous.
              </li>
              <li>
                <strong>Accéder à leurs Données.</strong> Les Utilisateurs ont
                le droit de savoir si les Données sont traitées par le
                Propriétaire, d'obtenir des informations sur certains aspects du
                traitement et d'obtenir une copie des Données en cours de
                traitement.
              </li>
              <li>
                <strong>Vérifier et obtenir une rectification.</strong> Les
                Utilisateurs ont le droit de vérifier l'exactitude de leurs
                Données et de demander qu'elles soient mises à jour ou
                corrigées.
              </li>
              <li>
                <strong>Limiter le traitement de leurs Données.</strong> Les
                Utilisateurs ont le droit, sous certaines conditions, de limiter
                le traitement de leurs Données. Dans ce cas, le Propriétaire
                traitera leurs Données uniquement pour les stocker.
              </li>
              <li>
                <strong>
                  Faire supprimer ou effacer leurs Données personnelles.
                </strong>{' '}
                Les Utilisateurs ont le droit, sous certaines conditions,
                d'obtenir l'effacement de leurs Données auprès du Propriétaire.
              </li>
              <li>
                <strong>
                  Récupérer leurs Données et les transférer à un autre
                  responsable du traitement.
                </strong>{' '}
                Les Utilisateurs ont le droit de récupérer leurs Données dans un
                format structuré, couramment utilisé et lisible par machine et,
                si cela est techniquement possible, de les transmettre à un
                autre responsable du traitement sans obstacle d'aucune sorte.
                Cette disposition s'applique, sous réserve que les Données
                soient traitées par des moyens automatisés et que le traitement
                repose sur le consentement de l'Utilisateur, sur un contrat
                auquel l'Utilisateur est partie ou sur des obligations
                précontractuelles.
              </li>
              <li>
                <strong>Déposer plainte.</strong> Les Utilisateurs ont le droit
                de déposer une plainte auprès de leur autorité compétente en
                matière de protection des données.
              </li>
            </ul>

            <h3>Informations concernant le droit d'opposition au traitement</h3>
            <p>
              Lorsque les Données personnelles sont traitées dans l'intérêt
              public, dans l'exercice d'une autorité officielle dévolue au
              Propriétaire ou aux fins des intérêts légitimes poursuivis par
              celui-ci, les Utilisateurs peuvent s'opposer à ce traitement en
              fournissant un motif lié à leur situation particulière devant
              justifier cette opposition.
            </p>
            <p>
              Les Utilisateurs doivent cependant savoir que si leurs Données
              personnelles sont traitées à des fins de marketing direct, ils
              peuvent s'opposer à ce traitement à tout moment sans aucune
              justification. Pour savoir si le Propriétaire traite des Données
              personnelles à des fins de marketing direct, les Utilisateurs
              peuvent se reporter aux sections correspondantes du présent
              document.
            </p>

            <h3>Comment exercer ces droits</h3>
            <p>
              Toute demande d'exercice des droits de l'Utilisateur peut être
              adressée au Propriétaire grâce aux coordonnées fournies dans le
              présent document. Ces demandes peuvent être exercées gratuitement
              et seront étudiées par le Propriétaire le plus tôt possible et
              toujours dans un délai d'un mois.
            </p>
            <Separator />
            <h2>Politique relative aux Cookies</h2>
            <p>
              Revibe utilise des Traqueurs. Pour en savoir plus, l'Utilisateur
              peut consulter la{' '}
              <Link to={COMPANY_ROUTES.COOKIE_POLICY}>
                Politique relative aux Cookies
              </Link>
              .
            </p>
            <Separator />
            <h2 id="further_data_processing_info">
              Informations supplémentaires sur le traitement et la collecte des
              Données
            </h2>
            <h3>Action en justice</h3>
            <p>
              Les Données personnelles de l'Utilisateur peuvent être utilisées à
              des fins juridiques par le Propriétaire devant les tribunaux ou
              dans les étapes pouvant conduire à une action en justice résultant
              d'une utilisation inappropriée de Revibe ou des Services connexes.
              <br />
              L'Utilisateur est conscient du fait que le Propriétaire peut être
              amené à révéler des Données personnelles à la demande des
              autorités publiques.
            </p>
            <h3>
              Informations supplémentaires concernant les Données personnelles
              de l'Utilisateur
            </h3>
            <p>
              Outre les informations contenues dans la présente politique de
              confidentialité, Revibe peut fournir à l'Utilisateur des
              renseignements complémentaires et des informations contextuelles
              concernant des services particuliers ou la collecte et le
              traitement des Données personnelles.
            </p>
            <h3>Journaux système et maintenance</h3>
            <p>
              À des fins d'exploitation et de maintenance, Revibe et tout
              service tiers peuvent collecter des fichiers qui enregistrent les
              interactions avec Revibe (journaux système) ou utiliser à cette
              fin d'autres Données personnelles (telles que l'adresse IP).
            </p>
            <h3>Informations non incluses dans la présente politique</h3>
            <p>
              De plus amples renseignements concernant la collecte ou le
              traitement des Données personnelles peuvent à tout moment être
              demandés au Propriétaire. Veuillez consulter les coordonnées
              figurant au début du présent document.
            </p>

            <h3>
              Comment les demandes «&nbsp;Ne pas pister&nbsp;» sont traitées
            </h3>
            <p>
              Revibe ne prend pas en charge les demandes «&nbsp;Ne pas
              pister&nbsp;».
              <br />
              Référez-vous à la politique de confidentialité des services tiers
              pour déterminer s'ils acceptent ou non aux demandes «&nbsp;Ne pas
              pister&nbsp;».
            </p>

            <h3>Modifications de la présente politique de confidentialité</h3>
            <p>
              Le Propriétaire se réserve le droit d'apporter des modifications à
              la présente politique de confidentialité, à tout moment, en
              informant ses Utilisateurs sur cette page et éventuellement dans
              cette Revibe ou - pour autant que cela soit techniquement et
              légalement possible - en envoyant une notification aux
              Utilisateurs par l'intermédiaire des coordonnées disponibles pour
              le Propriétaire. Il est fortement recommandé de consulter cette
              page fréquemment, en se référant à la date de la dernière
              modification indiquée en bas.
              <br />
              <br />
              Si les modifications influencent les activités de traitement
              effectuées sur la base du consentement de l'Utilisateur, le
              Propriétaire doit recueillir un nouveau consentement de
              l'Utilisateur lorsque nécessaire.
            </p>
            <Separator />
            <Accordion type="multiple">
              <AccordionItem value="legal">
                <AccordionTrigger>
                  Définitions et références légales
                </AccordionTrigger>
                <AccordionContent>
                  <SpacedContainer>
                    <h4>Données personnelles (ou Données)</h4>
                    <p>
                      Toute information qui, directement, indirectement ou en
                      relation avec d'autres informations - y compris un numéro
                      d'identification personnel - permet l'identification ou
                      l'identifiabilité d'une personne physique.
                    </p>

                    <h4>Données d'utilisation</h4>
                    <p>
                      Les informations collectées automatiquement par Revibe (ou
                      par des services tiers employés par Revibe), qui peuvent
                      inclure les adresses IP ou les noms de domaines des
                      ordinateurs utilisés par les Utilisateurs qui utilisent
                      Revibe, les adresses URI (Uniform Resource Identifier ou
                      identifiant uniforme de ressource), l'heure de la demande,
                      la méthode utilisée pour soumettre la demande au serveur,
                      la taille du fichier reçu en réponse, le code numérique
                      indiquant le statut de la réponse du serveur (résultat
                      favorable, erreur, etc.), le pays d'origine, les
                      caractéristiques du navigateur et du système
                      d'exploitation utilisés par l'Utilisateur, les différents
                      détails relatifs au temps par visite (p. ex. temps passé
                      sur chaque page dans l'Application) et les détails
                      relatifs au chemin suivi dans l'Application avec une
                      référence spéciale à la séquence des pages visitées, et
                      d'autres paramètres concernant le système d'exploitation
                      ou l'environnement informatique de l'Utilisateur.
                    </p>

                    <h4>Utilisateur</h4>
                    <p>
                      La personne utilisant Revibe qui, sauf indication
                      contraire, correspond à la Personne concernée.
                    </p>

                    <h4>Personne concernée</h4>
                    <p>
                      La personne physique à laquelle les Données personnelles
                      font référence.
                    </p>

                    <h4>Sous-traitant (ou Responsable des données)</h4>
                    <p>
                      La personne physique ou morale, l'autorité publique,
                      l'institution ou tout autre organisme qui traite les
                      Données personnelles pour le compte du Responsable du
                      traitement, tel que décrit dans la présente politique de
                      confidentialité.
                    </p>

                    <h4>Responsable du traitement (ou Propriétaire)</h4>
                    <p>
                      La personne physique ou morale, l'autorité publique,
                      l'institution ou toute autre organisme qui, seul ou
                      conjointement avec d'autres, détermine les finalités et
                      les moyens du traitement de Données personnelles, y
                      compris les mesures de sécurité concernant le
                      fonctionnement et l'utilisation de Revibe. Sauf mention
                      contraire, le Responsable du traitement est le
                      Propriétaire de Revibe.
                    </p>

                    <h4>Revibe (ou cette Application)</h4>
                    <p>
                      Les moyens par lesquels les Données personnelles de
                      l'Utilisateur sont collectées et traitées.
                    </p>

                    <h4>Service</h4>
                    <p>
                      Le service fourni par Revibe comme décrit dans les
                      conditions s'y rapportant (le cas échéant) et sur ce
                      site/cette application.
                    </p>

                    <h4>Union européenne (ou UE)</h4>
                    <p>
                      Sauf indication contraire, toutes les références faites
                      dans le présent document à l'Union européenne incluent
                      tous les États membres actuels de l'Union européenne et de
                      l'Espace économique européen.
                    </p>

                    <h4>Cookie</h4>
                    <p>
                      Les Cookies sont des Traqueurs constitués de petits
                      ensembles de données stockés dans le navigateur de
                      l'Utilisateur.
                    </p>

                    <h4>Traqueurs</h4>
                    <p>
                      Un Traqueur indique toute technologie - par exemple, les
                      Cookies, les identifiants uniques, les balises Web, les
                      scripts intégrés, les balises électroniques et les
                      empreintes digitales - permettant de suivre les
                      Utilisateurs, par exemple en accédant à des informations
                      ou en les stockant sur l'appareil de l'Utilisateur.
                    </p>
                    <Separator />
                    <h4>Informations légales</h4>
                    <p>
                      La présente politique de confidentialité a été préparée en
                      exécution des dispositions de plusieurs législations,
                      notamment de l'article 13/14 du règlement européen
                      2016/679 (règlement général sur la protection des
                      données).
                    </p>
                    <p>
                      Cette politique de confidentialité concerne uniquement
                      Revibe, sauf indication contraire dans le présent
                      document.
                    </p>
                  </SpacedContainer>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </SpacedContainer>
        </Layout>
      </ShopPage>
    );
  }

  return (
    <ShopPage
      title={tu('privacy-policy')}
      description="Your privacy is important to us. It is Revibe's policy to respect
    your privacy regarding any information we may collect from you
    across our website"
    >
      <Layout minHeight className="mx-auto max-w-5xl">
        <SpacedContainer>
          <h1>Privacy Policy</h1>
          <p>
            Privacy Policy of www.revibe-upcycling.com (Revibe). This Website
            collects some Personal Data from its Users.
          </p>
          <p>
            This document can be printed for reference by using the print
            command in the settings of any browser.
          </p>
          <Separator />
          <h2>Owner and Data Controller</h2>
          <p>REVIBE SAS</p>
          <p>9 RUE DAMRÉMONT, PARIS 75018, FRANCE</p>
          <p>
            <strong>Owner contact email: </strong>hello@revibe-upcycling.com
          </p>
          <Separator />
          <h2>Types of Data collected</h2>
          <p>
            Among the types of Personal Data that Revibe collects, by itself or
            through third parties, there are: Tracker; Usage Data; Data
            communicated while using the service.
          </p>
          <p>
            Complete details on each type of Personal Data collected are
            provided in the dedicated sections of this privacy policy or by
            specific explanation texts displayed prior to the Data collection.
          </p>
          <p>
            Personal Data may be freely provided by the User, or, in case of
            Usage Data, collected automatically when using Revibe.
          </p>
          <p>
            Unless specified otherwise, all Data requested by Revibe is
            mandatory and failure to provide this Data may make it impossible
            for Revibe to provide its services. In cases where this Application
            specifically states that some Data is not mandatory, Users are free
            not to communicate this Data without consequences to the
            availability or the functioning of the Service.
          </p>
          <p>
            Users who are uncertain about which Personal Data is mandatory are
            welcome to contact the Owner. Any use of Cookies - or of other
            tracking tools - by Revibe or by the owners of third-party services
            used by Revibe serves the purpose of providing the Service required
            by the User, in addition to any other purposes described in the
            present document and in the Cookie Policy, if available.
          </p>
          <p>
            Users are responsible for any third-party Personal Data obtained,
            published or shared through Revibe and confirm that they have the
            third party's consent to provide the Data to the Owner.
          </p>
          <Separator />
          <h2>Mode and place of processing the Data</h2>
          <h3>Methods of processing</h3>
          <p>
            The Owner takes appropriate security measures to prevent
            unauthorized access, disclosure, modification, or unauthorized
            destruction of the Data.
            <br />
            The Data processing is carried out using computers and/or IT enabled
            tools, following organizational procedures and modes strictly
            related to the purposes indicated. In addition to the Owner, in some
            cases, the Data may be accessible to certain types of persons in
            charge, involved with the operation of Revibe (administration,
            sales, marketing, legal, system administration) or external parties
            (such as third-party technical service providers, mail carriers,
            hosting providers, IT companies, communications agencies) appointed,
            if necessary, as Data Processors by the Owner. The updated list of
            these parties may be requested from the Owner at any time.
          </p>
          <h3>Legal basis of processing</h3>
          <p>
            The Owner may process Personal Data relating to Users if one of the
            following applies:
          </p>
          <ul>
            <li>
              Users have given their consent for one or more specific purposes.
              Note: Under some legislations the Owner may be allowed to process
              Personal Data until the User objects to such processing
              (“opt-out”), without having to rely on consent or any other of the
              following legal bases. This, however, does not apply, whenever the
              processing of Personal Data is subject to European data protection
              law;
            </li>
            <li>
              provision of Data is necessary for the performance of an agreement
              with the User and/or for any pre-contractual obligations thereof;
            </li>
            <li>
              processing is necessary for compliance with a legal obligation to
              which the Owner is subject;
            </li>
            <li>
              processing is related to a task that is carried out in the public
              interest or in the exercise of official authority vested in the
              Owner;
            </li>
            <li>
              processing is necessary for the purposes of the legitimate
              interests pursued by the Owner or by a third party.
            </li>
          </ul>
          <p>
            In any case, the Owner will gladly help to clarify the specific
            legal basis that applies to the processing, and in particular
            whether the provision of Personal Data is a statutory or contractual
            requirement, or a requirement necessary to enter into a contract.
          </p>
          <h3>Place</h3>
          <p>
            The Data is processed at the Owner's operating offices and in any
            other places where the parties involved in the processing are
            located.
            <br />
            Depending on the User's location, data transfers may involve
            transferring the User's Data to a country other than their own. To
            find out more about the place of processing of such transferred
            Data, Users can check the section containing details about the
            processing of Personal Data.
          </p>
          <p>
            Users are also entitled to learn about the legal basis of Data
            transfers to a country outside the European Union or to any
            international organization governed by public international law or
            set up by two or more countries, such as the UN, and about the
            security measures taken by the Owner to safeguard their Data.
            <br />
            If any such transfer takes place, Users can find out more by
            checking the relevant sections of this document or inquire with the
            Owner using the information provided in the contact section.
          </p>
          <h3>Retention time</h3>
          <p>
            Personal Data shall be processed and stored for as long as required
            by the purpose they have been collected for.
          </p>
          <p>Therefore:</p>
          <ul>
            <li>
              Personal Data collected for purposes related to the performance of
              a contract between the Owner and the User shall be retained until
              such contract has been fully performed.
            </li>
            <li>
              Personal Data collected for the purposes of the Owner's legitimate
              interests shall be retained as long as needed to fulfill such
              purposes. Users may find specific information regarding the
              legitimate interests pursued by the Owner within the relevant
              sections of this document or by contacting the Owner.
            </li>
          </ul>
          <p>
            The Owner may be allowed to retain Personal Data for a longer period
            whenever the User has given consent to such processing, as long as
            such consent is not withdrawn. Furthermore, the Owner may be obliged
            to retain Personal Data for a longer period whenever required to do
            so for the performance of a legal obligation or upon order of an
            authority.
            <br />
            Once the retention period expires, Personal Data shall be deleted.
            Therefore, the right of access, the right to erasure, the right to
            rectification and the right to data portability cannot be enforced
            after expiration of the retention period.
          </p>
          <Separator />
          <h2>The purposes of processing</h2>
          <p>
            The Data concerning the User is collected to allow the Owner to
            provide its Service, comply with its legal obligations, respond to
            enforcement requests, protect its rights and interests (or those of
            its Users or third parties), detect any malicious or fraudulent
            activity, as well as the following: Analytics, Registration and
            authentication and Interaction with live chat platforms.
          </p>

          <p>
            For specific information about the Personal Data used for each
            purpose, the User may refer to the section “Detailed information on
            the processing of Personal Data”.
          </p>
          <Separator />
          <h2>Detailed information on the processing of Personal Data</h2>
          <p>
            Personal Data is collected for the following purposes and using the
            following services:
          </p>
          <Accordion type="multiple">
            <AccordionItem value="analytics">
              <AccordionTrigger>Analytics</AccordionTrigger>
              <AccordionContent>
                <SpacedContainer>
                  <p>
                    The services contained in this section enable the Owner to
                    monitor and analyze web traffic and can be used to keep
                    track of User behavior.
                  </p>

                  <h4>Google Analytics (Google Ireland Limited)</h4>
                  <div>
                    <p>
                      Google Analytics is a web analysis service provided by
                      Google Ireland Limited (“Google”). Google utilizes the
                      Data collected to track and examine the use of Revibe, to
                      prepare reports on its activities and share them with
                      other Google services.
                      <br />
                      Google may use the Data collected to contextualize and
                      personalize the ads of its own advertising network.
                    </p>
                  </div>

                  <p>Personal Data processed: Tracker; Usage Data.</p>

                  <p>
                    Place of processing: Ireland -
                    <Link
                      to="https://policies.google.com/privacy"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                    -
                    <Link
                      to="https://tools.google.com/dlpage/gaoptout?hl=en"
                      target="_blank"
                    >
                      Opt Out
                    </Link>
                    .
                  </p>

                  <h4>
                    Facebook Ads conversion tracking (Facebook pixel) (Meta
                    Platforms Ireland Limited)
                  </h4>
                  <div>
                    <p>
                      Facebook Ads conversion tracking (Facebook pixel) is an
                      analytics service provided by Meta Platforms Ireland
                      Limited that connects data from the Meta advertising
                      network with actions performed on Revibe. The Facebook
                      pixel tracks conversions that can be attributed to ads on
                      Facebook, Instagram and Audience Network.
                    </p>
                  </div>

                  <p>Personal Data processed: Tracker; Usage Data.</p>

                  <p>
                    Place of processing: Ireland -
                    <Link
                      to="https://www.facebook.com/about/privacy/"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </SpacedContainer>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="auth">
              <AccordionTrigger>
                Registration and authentication
              </AccordionTrigger>
              <AccordionContent>
                <SpacedContainer>
                  <p>
                    By registering or authenticating, Users allow Revibe to
                    identify them and give them access to dedicated services.
                    <br />
                    Depending on what is described below, third parties may
                    provide registration and authentication services. In this
                    case, Revibe will be able to access some Data, stored by
                    these third-party services, for registration or
                    identification purposes. <br />
                    Some of the services listed below may also collect Personal
                    Data for targeting and profiling purposes; to find out more,
                    please refer to the description of each service.
                  </p>

                  <h4>
                    Facebook Authentication (Meta Platforms Ireland Limited)
                  </h4>
                  <div>
                    <p>
                      Facebook Authentication is a registration and
                      authentication service provided by Meta Platforms Ireland
                      Limited and is connected to the Facebook social network.
                    </p>
                  </div>

                  <p>
                    Personal Data processed: Tracker; various types of Data as
                    specified in the privacy policy of the service.
                  </p>

                  <p>
                    Place of processing: Ireland -
                    <Link
                      to="https://www.facebook.com/help/405977429438260"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </SpacedContainer>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Separator />
          <h2>The rights of Users</h2>
          <p>
            Users may exercise certain rights regarding their Data processed by
            the Owner.
          </p>
          <p>In particular, Users have the right to do the following:</p>

          <ul>
            <li>
              <strong>Withdraw their consent at any time.</strong> Users have
              the right to withdraw consent where they have previously given
              their consent to the processing of their Personal Data.
            </li>
            <li>
              <strong>Object to processing of their Data.</strong> Users have
              the right to object to the processing of their Data if the
              processing is carried out on a legal basis other than consent.
              Further details are provided in the dedicated section below.
            </li>
            <li>
              <strong>Access their Data.</strong> Users have the right to learn
              if Data is being processed by the Owner, obtain disclosure
              regarding certain aspects of the processing and obtain a copy of
              the Data undergoing processing.
            </li>
            <li>
              <strong>Verify and seek rectification.</strong> Users have the
              right to verify the accuracy of their Data and ask for it to be
              updated or corrected.
            </li>
            <li>
              <strong>Restrict the processing of their Data.</strong> Users have
              the right, under certain circumstances, to restrict the processing
              of their Data. In this case, the Owner will not process their Data
              for any purpose other than storing it.
            </li>
            <li>
              <strong>
                Have their Personal Data deleted or otherwise removed.
              </strong>
              Users have the right, under certain circumstances, to obtain the
              erasure of their Data from the Owner.
            </li>
            <li>
              <strong>
                Receive their Data and have it transferred to another
                controller.
              </strong>
              Users have the right to receive their Data in a structured,
              commonly used and machine readable format and, if technically
              feasible, to have it transmitted to another controller without any
              hindrance. This provision is applicable provided that the Data is
              processed by automated means and that the processing is based on
              the User's consent, on a contract which the User is part of or on
              pre-contractual obligations thereof.
            </li>
            <li>
              <strong>Lodge a complaint.</strong> Users have the right to bring
              a claim before their competent data protection authority.
            </li>
          </ul>

          <h3>Details about the right to object to processing</h3>
          <p>
            Where Personal Data is processed for a public interest, in the
            exercise of an official authority vested in the Owner or for the
            purposes of the legitimate interests pursued by the Owner, Users may
            object to such processing by providing a ground related to their
            particular situation to justify the objection.
          </p>
          <p>
            Users must know that, however, should their Personal Data be
            processed for direct marketing purposes, they can object to that
            processing at any time without providing any justification. To
            learn, whether the Owner is processing Personal Data for direct
            marketing purposes, Users may refer to the relevant sections of this
            document.
          </p>

          <h3>How to exercise these rights</h3>
          <p>
            Any requests to exercise User rights can be directed to the Owner
            through the contact details provided in this document. These
            requests can be exercised free of charge and will be addressed by
            the Owner as early as possible and always within one month.
          </p>
          <Separator />
          <h2>Cookie Policy</h2>
          <p>
            Revibe uses Trackers. To learn more, the User may consult the{' '}
            <Link to={COMPANY_ROUTES.COOKIE_POLICY}>Cookie Policy</Link>.
          </p>
          <Separator />
          <h2>Additional information about Data collection and processing</h2>
          <h3>Legal action</h3>
          <p>
            The User's Personal Data may be used for legal purposes by the Owner
            in Court or in the stages leading to possible legal action arising
            from improper use of Revibe or the related Services.
            <br />
            The User declares to be aware that the Owner may be required to
            reveal personal data upon request of public authorities.
          </p>
          <h3>Additional information about User's Personal Data</h3>
          <p>
            In addition to the information contained in this privacy policy,
            Revibe may provide the User with additional and contextual
            information concerning particular Services or the collection and
            processing of Personal Data upon request.
          </p>
          <h3>System logs and maintenance</h3>
          <p>
            For operation and maintenance purposes, Revibe and any third-party
            services may collect files that record interaction with Revibe
            (System logs) use other Personal Data (such as the IP Address) for
            this purpose.
          </p>
          <h3>Information not contained in this policy</h3>
          <p>
            More details concerning the collection or processing of Personal
            Data may be requested from the Owner at any time. Please see the
            contact information at the beginning of this document.
          </p>

          <h3>How “Do Not Track” requests are handled</h3>
          <p>
            Revibe does not support “Do Not Track” requests.
            <br />
            To determine whether any of the third-party services it uses honor
            the “Do Not Track” requests, please read their privacy policies.
          </p>

          <h3>Changes to this privacy policy</h3>
          <p>
            The Owner reserves the right to make changes to this privacy policy
            at any time by notifying its Users on this page and possibly within
            Revibe and/or - as far as technically and legally feasible - sending
            a notice to Users via any contact information available to the
            Owner. It is strongly recommended to check this page often,
            referring to the date of the last modification listed at the bottom.{' '}
            <br />
            <br />
            Should the changes affect processing activities performed on the
            basis of the User's consent, the Owner shall collect new consent
            from the User, where required.
          </p>
          <Separator />
          <Accordion type="multiple">
            <AccordionItem value="legal">
              <AccordionTrigger>
                Definition and legal references
              </AccordionTrigger>
              <AccordionContent>
                <div>
                  <h4>Personal Data (or Data)</h4>
                  <p>
                    Any information that directly, indirectly, or in connection
                    with other information — including a personal identification
                    number — allows for the identification or identifiability of
                    a natural person.
                  </p>

                  <h4>Usage Data</h4>
                  <p>
                    Information collected automatically through Revibe (or
                    third-party services employed in Revibe), which can include:
                    the IP addresses or domain names of the computers utilized
                    by the Users who use Revibe, the URI addresses (Uniform
                    Resource Identifier), the time of the request, the method
                    utilized to submit the request to the server, the size of
                    the file received in response, the numerical code indicating
                    the status of the server's answer (successful outcome,
                    error, etc.), the country of origin, the features of the
                    browser and the operating system utilized by the User, the
                    various time details per visit (e.g., the time spent on each
                    page within the Application) and the details about the path
                    followed within the Application with special reference to
                    the sequence of pages visited, and other parameters about
                    the device operating system and/or the User's IT
                    environment.
                  </p>

                  <h4>User</h4>
                  <p>
                    The individual using Revibe who, unless otherwise specified,
                    coincides with the Data Subject.
                  </p>

                  <h4>Data Subject</h4>
                  <p>The natural person to whom the Personal Data refers.</p>

                  <h4>Data Processor (or Data Supervisor)</h4>
                  <p>
                    The natural or legal person, public authority, agency or
                    other body which processes Personal Data on behalf of the
                    Controller, as described in this privacy policy.
                  </p>

                  <h4>Data Controller (or Owner)</h4>
                  <p>
                    The natural or legal person, public authority, agency or
                    other body which, alone or jointly with others, determines
                    the purposes and means of the processing of Personal Data,
                    including the security measures concerning the operation and
                    use of Revibe. The Data Controller, unless otherwise
                    specified, is the Owner of Revibe.
                  </p>

                  <h4>Revibe (or this Application)</h4>
                  <p>
                    The means by which the Personal Data of the User is
                    collected and processed.
                  </p>

                  <h4>Service</h4>
                  <p>
                    The service provided by Revibe as described in the relative
                    terms (if available) and on this site/application.
                  </p>

                  <h4>European Union (or EU)</h4>
                  <p>
                    Unless otherwise specified, all references made within this
                    document to the European Union include all current member
                    states to the European Union and the European Economic Area.
                  </p>

                  <h4>Cookie</h4>
                  <p>
                    Cookies are Trackers consisting of small sets of data stored
                    in the User's browser.
                  </p>

                  <h4>Tracker</h4>
                  <p>
                    Tracker indicates any technology - e.g Cookies, unique
                    identifiers, web beacons, embedded scripts, e-tags and
                    fingerprinting - that enables the tracking of Users, for
                    example by accessing or storing information on the User's
                    device.
                  </p>
                  <Separator />
                  <h4>Legal information</h4>
                  <p>
                    This privacy statement has been prepared based on provisions
                    of multiple legislations, including Art. 13/14 of Regulation
                    (EU) 2016/679 (General Data Protection Regulation).
                  </p>
                  <p>
                    This privacy policy relates solely to Revibe, if not stated
                    otherwise within this document.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Separator />
          <p>Latest update: March 31, 2022</p>
          <span>
            <Button onClick={() => router.push('/')}>Go back to home</Button>
          </span>
        </SpacedContainer>
      </Layout>
    </ShopPage>
  );
};

export default PrivacyPolicyPage;
